import React, { useEffect, useState } from "react";
import { Table, Tag, Popconfirm } from "antd";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getLocalStorageData } from "../../utils/LocalStorageService";
import { alertMessage } from "../../utils/AlertService";
import { useAppSelector } from "../../redux-toolkit/hooks";
import { BsPlus, BsPencilSquare, BsTrash } from "react-icons/bs";

// ── Services ──────────────────────────────────────────────────────────────────
import { useGetAllProjectsMutation, useDeleteProjectMutation } from "../../redux-toolkit/services/projectIntegrationService";
import { useGetAllCarbonSourceMasterMutation, useDeleteCarbonSourceMasterMutation } from "../../redux-toolkit/services/carbonSourceMasterService";
import { useGetAllMonitoringBatchMutation, useDeleteMonitoringBatchMutation } from "../../redux-toolkit/services/monitoringBatchService";
import { useGetAllVerificationRecordMutation, useDeleteVerificationRecordMutation } from "../../redux-toolkit/services/verificationRecordService";
import { useGetAllMintingTransactionMutation, useDeleteMintingTransactionMutation } from "../../redux-toolkit/services/mintingTransactionService";
import { useGetAllCarbonInventoryMutation, useDeleteCarbonInventoryMutation } from "../../redux-toolkit/services/carbonInventoryService";
import { useGetAllCarbonSaleTransactionMutation, useDeleteCarbonSaleTransactionMutation } from "../../redux-toolkit/services/carbonSaleTransactionService";
import { useGetAllRetirementTransactionMutation, useDeleteRetirementTransactionMutation } from "../../redux-toolkit/services/retirementTransactionService";
import { useGetAllFamilyOfficeAllocationMutation, useDeleteFamilyOfficeAllocationMutation } from "../../redux-toolkit/services/familyOfficeAllocationService";

// ── Slices ─────────────────────────────────────────────────────────────────────
import { setProjectIntegration, getAllProjectDetails } from "../../redux-toolkit/slices/projectIntegrationSlice";
import { setCarbonSourceMaster, getAllCarbonSourceMasterDetails } from "../../redux-toolkit/slices/carbonSourceMasterSlice";
import { setMonitoringBatch, getAllMonitoringBatchDetails } from "../../redux-toolkit/slices/monitoringBatchSlice";
import { setVerificationRecord, getAllVerificationRecordDetails } from "../../redux-toolkit/slices/verificationRecordSlice";
import { setMintingTransaction, getAllMintingTransactionDetails } from "../../redux-toolkit/slices/mintingTransactionSlice";
import { setCarbonInventory, getAllCarbonInventoryDetails } from "../../redux-toolkit/slices/carbonInventorySlice";
import { setCarbonSaleTransaction, getAllCarbonSaleTransactionDetails } from "../../redux-toolkit/slices/carbonSaleTransactionSlice";
import { setRetirementTransaction, getAllRetirementTransactionDetails } from "../../redux-toolkit/slices/retirementTransactionSlice";
import { setFamilyOfficeAllocation, getAllFamilyOfficeAllocationDetails } from "../../redux-toolkit/slices/familyOfficeAllocationSlice";

import "./executiveSummary.scss";

// ────────────────────────────────────────────────────────────────────────────────
// HELPERS
// ────────────────────────────────────────────────────────────────────────────────
const toArr = (val: any): any[] => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (val?.data && Array.isArray(val.data)) return val.data;
    if (val?.content && Array.isArray(val.content)) return val.content;
    return [];
};

// ────────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ────────────────────────────────────────────────────────────────────────────────
const ExecutiveSummary: React.FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const localUserObj: any = getLocalStorageData("localUser");
    const token = localUserObj?.token;

    // ── Redux selectors ──
    const projects = toArr(useAppSelector(getAllProjectDetails));
    const carbonSources = toArr(useAppSelector(getAllCarbonSourceMasterDetails));
    const monitoringBatches = toArr(useAppSelector(getAllMonitoringBatchDetails));
    const verificationRecords = toArr(useAppSelector(getAllVerificationRecordDetails));
    const mintingTransactions = toArr(useAppSelector(getAllMintingTransactionDetails));
    const carbonInventory = toArr(useAppSelector(getAllCarbonInventoryDetails));
    const carbonSaleTransactions = toArr(useAppSelector(getAllCarbonSaleTransactionDetails));
    const retirementTransactions = toArr(useAppSelector(getAllRetirementTransactionDetails));
    const familyOfficeAllocations = toArr(useAppSelector(getAllFamilyOfficeAllocationDetails));

    // ── Fetch mutations ──
    const [getAllProjects, allProjectsResponse] = useGetAllProjectsMutation();
    const [getAllCarbonSourceMaster, allCarbonSourceMasterResponse] = useGetAllCarbonSourceMasterMutation();
    const [getAllMonitoringBatch, allMonitoringBatchResponse] = useGetAllMonitoringBatchMutation();
    const [getAllVerificationRecord, allVerificationRecordResponse] = useGetAllVerificationRecordMutation();
    const [getAllMintingTransaction, allMintingTransactionResponse] = useGetAllMintingTransactionMutation();
    const [getAllCarbonInventory, allCarbonInventoryResponse] = useGetAllCarbonInventoryMutation();
    const [getAllCarbonSaleTransaction, allCarbonSaleTransactionResponse] = useGetAllCarbonSaleTransactionMutation();
    const [getAllRetirementTransaction, allRetirementTransactionResponse] = useGetAllRetirementTransactionMutation();
    const [getAllFamilyOfficeAllocation, allFamilyOfficeAllocationResponse] = useGetAllFamilyOfficeAllocationMutation();

    // ── Delete mutations ──
    const [deleteProject, deleteProjectResponse] = useDeleteProjectMutation();
    const [deleteCarbonSourceMaster, deleteCarbonSourceMasterResponse] = useDeleteCarbonSourceMasterMutation();
    const [deleteMonitoringBatch, deleteMonitoringBatchResponse] = useDeleteMonitoringBatchMutation();
    const [deleteVerificationRecord, deleteVerificationRecordResponse] = useDeleteVerificationRecordMutation();
    const [deleteMintingTransaction, deleteMintingTransactionResponse] = useDeleteMintingTransactionMutation();
    const [deleteCarbonInventory, deleteCarbonInventoryResponse] = useDeleteCarbonInventoryMutation();
    const [deleteCarbonSaleTransaction, deleteCarbonSaleTransactionResponse] = useDeleteCarbonSaleTransactionMutation();
    const [deleteRetirementTransaction, deleteRetirementTransactionResponse] = useDeleteRetirementTransactionMutation();
    const [deleteFamilyOfficeAllocation, deleteFamilyOfficeAllocationResponse] = useDeleteFamilyOfficeAllocationMutation();

    // ── Fetch all data ──
    const fetchAll = () => {
        const bigPage = { pageNumber: 0, offset: 500 };
        getAllProjects({ token, page: 0, offset: 500 });
        getAllCarbonSourceMaster({ token, payload: bigPage });
        getAllMonitoringBatch({ token, page: 0, size: 500 });
        getAllVerificationRecord({ token, page: 0, size: 500 });
        getAllMintingTransaction({ token, payload: bigPage });
        getAllCarbonInventory({ token, payload: bigPage });
        getAllCarbonSaleTransaction({ token, payload: bigPage });
        getAllRetirementTransaction({ token, payload: bigPage });
        getAllFamilyOfficeAllocation({ token, payload: bigPage });
    };

    useEffect(() => { fetchAll(); }, []);

    // ── Dispatch fetch responses ──
    useEffect(() => { if (allProjectsResponse.isSuccess) dispatch(setProjectIntegration(allProjectsResponse)); }, [allProjectsResponse.isSuccess]);
    useEffect(() => { if (allCarbonSourceMasterResponse.isSuccess) dispatch(setCarbonSourceMaster(allCarbonSourceMasterResponse)); }, [allCarbonSourceMasterResponse.isSuccess]);
    useEffect(() => { if (allMonitoringBatchResponse.isSuccess) dispatch(setMonitoringBatch(allMonitoringBatchResponse)); }, [allMonitoringBatchResponse.isSuccess]);
    useEffect(() => { if (allVerificationRecordResponse.isSuccess) dispatch(setVerificationRecord(allVerificationRecordResponse)); }, [allVerificationRecordResponse.isSuccess]);
    useEffect(() => { if (allMintingTransactionResponse.isSuccess) dispatch(setMintingTransaction(allMintingTransactionResponse)); }, [allMintingTransactionResponse.isSuccess]);
    useEffect(() => { if (allCarbonInventoryResponse.isSuccess) dispatch(setCarbonInventory(allCarbonInventoryResponse)); }, [allCarbonInventoryResponse.isSuccess]);
    useEffect(() => { if (allCarbonSaleTransactionResponse.isSuccess) dispatch(setCarbonSaleTransaction(allCarbonSaleTransactionResponse)); }, [allCarbonSaleTransactionResponse.isSuccess]);
    useEffect(() => { if (allRetirementTransactionResponse.isSuccess) dispatch(setRetirementTransaction(allRetirementTransactionResponse)); }, [allRetirementTransactionResponse.isSuccess]);
    useEffect(() => { if (allFamilyOfficeAllocationResponse.isSuccess) dispatch(setFamilyOfficeAllocation(allFamilyOfficeAllocationResponse)); }, [allFamilyOfficeAllocationResponse.isSuccess]);

    // ── Delete success/error handlers ──
    useEffect(() => {
        if (deleteProjectResponse.isSuccess) { alertMessage("Project deleted", "success"); fetchAll(); }
        if (deleteProjectResponse.isError) alertMessage("Error deleting project", "error");
    }, [deleteProjectResponse.isSuccess, deleteProjectResponse.isError]);

    useEffect(() => {
        if (deleteCarbonSourceMasterResponse.isSuccess) { alertMessage("Carbon Source deleted", "success"); fetchAll(); }
        if (deleteCarbonSourceMasterResponse.isError) alertMessage("Error deleting Carbon Source", "error");
    }, [deleteCarbonSourceMasterResponse.isSuccess, deleteCarbonSourceMasterResponse.isError]);

    useEffect(() => {
        if (deleteMonitoringBatchResponse.isSuccess) { alertMessage("Monitoring Batch deleted", "success"); fetchAll(); }
        if (deleteMonitoringBatchResponse.isError) alertMessage("Error deleting Monitoring Batch", "error");
    }, [deleteMonitoringBatchResponse.isSuccess, deleteMonitoringBatchResponse.isError]);

    useEffect(() => {
        if (deleteVerificationRecordResponse.isSuccess) { alertMessage("Verification Record deleted", "success"); fetchAll(); }
        if (deleteVerificationRecordResponse.isError) alertMessage("Error deleting Verification Record", "error");
    }, [deleteVerificationRecordResponse.isSuccess, deleteVerificationRecordResponse.isError]);

    useEffect(() => {
        if (deleteMintingTransactionResponse.isSuccess) { alertMessage("Minting Transaction deleted", "success"); fetchAll(); }
        if (deleteMintingTransactionResponse.isError) alertMessage("Error deleting Minting Transaction", "error");
    }, [deleteMintingTransactionResponse.isSuccess, deleteMintingTransactionResponse.isError]);

    useEffect(() => {
        if (deleteCarbonInventoryResponse.isSuccess) { alertMessage("Carbon Inventory deleted", "success"); fetchAll(); }
        if (deleteCarbonInventoryResponse.isError) alertMessage("Error deleting Carbon Inventory", "error");
    }, [deleteCarbonInventoryResponse.isSuccess, deleteCarbonInventoryResponse.isError]);

    useEffect(() => {
        if (deleteCarbonSaleTransactionResponse.isSuccess) { alertMessage("Sale Transaction deleted", "success"); fetchAll(); }
        if (deleteCarbonSaleTransactionResponse.isError) alertMessage("Error deleting Sale Transaction", "error");
    }, [deleteCarbonSaleTransactionResponse.isSuccess, deleteCarbonSaleTransactionResponse.isError]);

    useEffect(() => {
        if (deleteRetirementTransactionResponse.isSuccess) { alertMessage("Retirement Transaction deleted", "success"); fetchAll(); }
        if (deleteRetirementTransactionResponse.isError) alertMessage("Error deleting Retirement Transaction", "error");
    }, [deleteRetirementTransactionResponse.isSuccess, deleteRetirementTransactionResponse.isError]);

    useEffect(() => {
        if (deleteFamilyOfficeAllocationResponse.isSuccess) { alertMessage("Allocation deleted", "success"); fetchAll(); }
        if (deleteFamilyOfficeAllocationResponse.isError) alertMessage("Error deleting Allocation", "error");
    }, [deleteFamilyOfficeAllocationResponse.isSuccess, deleteFamilyOfficeAllocationResponse.isError]);

    // ── Build action column ──
    const makeActionCol = (
        editPath: string,
        deleteFn: (id: any) => void,
        label: string
    ): any => ({
        title: "ACTIONS",
        dataIndex: "actions",
        key: "actions",
        fixed: "right" as const,
        width: 130,
        render: (_: any, record: any) => (
            <div className="exec-summary__row-actions">
                <button
                    className="exec-summary__action-btn exec-summary__action-btn--edit"
                    onClick={() => navigate(editPath)}
                    title={`Edit ${label}`}
                >
                    <BsPencilSquare size={14} />
                    Edit
                </button>
                <Popconfirm
                    title={`Delete this ${label}?`}
                    okText="Yes"
                    cancelText="No"
                    onConfirm={() => deleteFn(record.id)}
                >
                    <button
                        className="exec-summary__action-btn exec-summary__action-btn--delete"
                        title={`Delete ${label}`}
                    >
                        <BsTrash size={14} />
                        Delete
                    </button>
                </Popconfirm>
            </div>
        ),
    });

    // ── Column definitions ──
    const PROJECT_COLS: any[] = [
        { dataIndex: "id", title: "PROJECT ID", width: 120 },
        { dataIndex: "name", title: "PROJECT NAME", width: 180, render: (val: any) => <b>{val || "-"}</b> },
        { dataIndex: "projectCode", title: "METHODOLOGY", width: 130 },
        { dataIndex: "startDate", title: "START DATE", width: 130 },
        { dataIndex: "status", title: "STATUS", width: 110, render: (val: any) => val ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag> },
        makeActionCol("/carbon-master/carbon-project-master", (id) => deleteProject({ token, id }), "Project"),
    ];

    const SOURCE_COLS: any[] = [
        { dataIndex: "id", title: "SOURCE ID", width: 110 },
        { dataIndex: "projectId", title: "PROJECT ID", width: 110, render: (_: any, r: any) => r?.project?.id || r?.projectId || "-" },
        { dataIndex: "assetRef", title: "ASSET REF", width: 140 },
        { dataIndex: "areaHa", title: "AREA (HA)", width: 110 },
        { dataIndex: "baselineCarbon", title: "BASELINE", width: 130 },
        makeActionCol("/carbon-master/carbon-source-master", (id) => deleteCarbonSourceMaster({ token, id }), "Carbon Source"),
    ];

    const MONITORING_COLS: any[] = [
        { dataIndex: "id", title: "BATCH ID", width: 110 },
        { dataIndex: "sourceId", title: "SOURCE ID", width: 110, render: (_: any, r: any) => r?.sourceMaster?.id || r?.sourceId || "-" },
        { dataIndex: "period", title: "PERIOD", width: 190, render: (_: any, r: any) => `${r?.periodStart || ""} – ${r?.periodEnd || ""}` },
        { dataIndex: "measuredCarbon", title: "MEASURED CARBON", width: 160 },
        { dataIndex: "sequestration", title: "SEQUESTRATION", width: 150 },
        { dataIndex: "status", title: "STATUS", width: 110, render: (val: any) => val ? <Tag color="blue">Verified</Tag> : <Tag color="orange">Pending</Tag> },
        makeActionCol("/carbon-master/monitoring-batch", (id) => deleteMonitoringBatch({ token, id }), "Monitoring Batch"),
    ];

    const VERIFICATION_COLS: any[] = [
        { dataIndex: "id", title: "VERIFICATION ID", width: 150 },
        { dataIndex: "verifierName", title: "VERIFIER", width: 150 },
        { dataIndex: "verificationDate", title: "DATE", width: 130 },
        { dataIndex: "approvedQty", title: "APPROVED", width: 110 },
        { dataIndex: "bufferPercentage", title: "BUFFER %", width: 110 },
        { dataIndex: "issuable", title: "ISSUABLE", width: 110, render: (val: any) => <b>{val ?? "-"}</b> },
        makeActionCol("/carbon-master/verification-record", (id) => deleteVerificationRecord({ token, id }), "Verification Record"),
    ];

    const MINTING_COLS: any[] = [
        { dataIndex: "id", title: "MINT TXN ID", width: 130 },
        { dataIndex: "vintage", title: "VINTAGE", width: 100 },
        { dataIndex: "mintDate", title: "MINT DATE", width: 130 },
        { dataIndex: "quantity", title: "QUANTITY", width: 110 },
        { dataIndex: "registryRef", title: "REGISTRY REF", width: 160 },
        makeActionCol("/transactions/minting-transaction", (id) => deleteMintingTransaction({ token, id }), "Minting Transaction"),
    ];

    const INVENTORY_COLS: any[] = [
        { dataIndex: "id", title: "INVENTORY ID", width: 130 },
        { dataIndex: "projectId", title: "PROJECT ID", width: 110, render: (_: any, r: any) => r?.project?.id || r?.projectId || "-" },
        { dataIndex: "vintage", title: "VINTAGE", width: 100 },
        {
            dataIndex: "status", title: "STATUS", width: 110,
            render: (val: any) => {
                if (!val) return "-";
                const lower = String(val).toLowerCase();
                if (lower === "available") return <Tag color="green">{val}</Tag>;
                if (lower === "sold") return <Tag color="orange">{val}</Tag>;
                return <Tag>{val}</Tag>;
            }
        },
        { dataIndex: "quantity", title: "QUANTITY", width: 110 },
        { dataIndex: "lastUpdated", title: "LAST UPDATED", width: 140 },
        makeActionCol("/transactions/carbon-inventory", (id) => deleteCarbonInventory({ token, id }), "Carbon Inventory"),
    ];

    const SALE_COLS: any[] = [
        { dataIndex: "id", title: "SALE ID", width: 120 },
        { dataIndex: "buyer", title: "BUYER", width: 160 },
        { dataIndex: "date", title: "DATE", width: 130 },
        { dataIndex: "qty", title: "QTY", width: 90 },
        { dataIndex: "price", title: "PRICE", width: 100 },
        { dataIndex: "totalValue", title: "TOTAL VALUE", width: 130, render: (val: any) => val != null ? <b>${val}</b> : "-" },
        makeActionCol("/transactions/carbon-sale-transaction", (id) => deleteCarbonSaleTransaction({ token, id }), "Sale Transaction"),
    ];

    const RETIREMENT_COLS: any[] = [
        { dataIndex: "id", title: "RETIRE ID", width: 120 },
        { dataIndex: "retirementDate", title: "RETIREMENT DATE", width: 160 },
        { dataIndex: "quantity", title: "QUANTITY", width: 110 },
        { dataIndex: "reason", title: "REASON", width: 170 },
        { dataIndex: "registryProof", title: "REGISTRY PROOF", width: 170 },
        makeActionCol("/transactions/retirement-transaction", (id) => deleteRetirementTransaction({ token, id }), "Retirement Transaction"),
    ];

    const FAMILY_COLS: any[] = [
        { dataIndex: "id", title: "ALLOCATION ID", width: 140 },
        { dataIndex: "investor", title: "INVESTOR", width: 160 },
        { dataIndex: "projectId", title: "PROJECT", width: 130, render: (_: any, r: any) => r?.project?.name || r?.projectId || "-" },
        { dataIndex: "credits", title: "CREDITS", width: 110 },
        { dataIndex: "revenueShare", title: "REVENUE SHARE", width: 150 },
        { dataIndex: "date", title: "DATE", width: 120 },
        makeActionCol("/family-office/family-office-allocation", (id) => deleteFamilyOfficeAllocation({ token, id }), "Allocation"),
    ];

    // ── Computed metrics ──
    const totalProjects = projects.length;
    const landUnderMgmt = carbonSources.reduce((sum: number, s: any) => sum + (Number(s?.areaHa) || 0), 0);
    const creditsMinted = mintingTransactions.reduce((sum: number, m: any) => sum + (Number(m?.quantity) || 0), 0);
    const creditsSold = carbonSaleTransactions.reduce((sum: number, s: any) => sum + (Number(s?.qty) || 0), 0);
    const creditsRetired = retirementTransactions.reduce((sum: number, r: any) => sum + (Number(r?.quantity) || 0), 0);
    const availableCredits = Math.max(0, creditsMinted - creditsSold - creditsRetired);

    const summaryCards = [
        { label: "TOTAL PROJECTS", value: totalProjects, suffix: "" },
        { label: "LAND UNDER MANAGEMENT", value: landUnderMgmt.toLocaleString(), suffix: " ha" },
        { label: "CREDITS MINTED", value: creditsMinted.toLocaleString(), suffix: "" },
        { label: "AVAILABLE CREDITS", value: availableCredits.toLocaleString(), suffix: "" },
        { label: "CREDITS SOLD", value: creditsSold.toLocaleString(), suffix: "" },
        { label: "CREDITS RETIRED", value: creditsRetired.toLocaleString(), suffix: "" },
    ];

    // ── Section config ──
    const tableConfig = [
        { title: "Carbon Project Master", data: projects, cols: PROJECT_COLS, loading: allProjectsResponse.isLoading, addPath: "/carbon-master/carbon-project-master", addLabel: "Create Project" },
        { title: "Carbon Source Master", data: carbonSources, cols: SOURCE_COLS, loading: allCarbonSourceMasterResponse.isLoading, addPath: "/carbon-master/carbon-source-master", addLabel: "Register Land Parcel" },
        { title: "Monitoring Batches", data: monitoringBatches, cols: MONITORING_COLS, loading: allMonitoringBatchResponse.isLoading, addPath: "/carbon-master/monitoring-batch", addLabel: "New Measurement" },
        { title: "Verification Records", data: verificationRecords, cols: VERIFICATION_COLS, loading: allVerificationRecordResponse.isLoading, addPath: "/carbon-master/verification-record", addLabel: "Add Verification" },
        { title: "Minting Transactions", data: mintingTransactions, cols: MINTING_COLS, loading: allMintingTransactionResponse.isLoading, addPath: "/transactions/minting-transaction", addLabel: "Mint Credits" },
        { title: "Carbon Inventory", data: carbonInventory, cols: INVENTORY_COLS, loading: allCarbonInventoryResponse.isLoading, addPath: "/transactions/carbon-inventory", addLabel: "Add Inventory" },
        { title: "Carbon Sale Transactions", data: carbonSaleTransactions, cols: SALE_COLS, loading: allCarbonSaleTransactionResponse.isLoading, addPath: "/transactions/carbon-sale-transaction", addLabel: "New Sale" },
        { title: "Retirement Transactions", data: retirementTransactions, cols: RETIREMENT_COLS, loading: allRetirementTransactionResponse.isLoading, addPath: "/transactions/retirement-transaction", addLabel: "Retire Credits" },
        { title: "Family Office: Investor Allocation", data: familyOfficeAllocations, cols: FAMILY_COLS, loading: allFamilyOfficeAllocationResponse.isLoading, addPath: "/family-office/family-office-allocation", addLabel: "Add Allocation" },
    ];

    const projectNames = projects.map((p: any) => p?.name || p?.projectCode).filter(Boolean).join(" | ");

    return (
        <div className="exec-summary">
            {/* Header */}
            <div className="exec-summary__header">
                <div>
                    <h2 className="exec-summary__title">Carbon Lifecycle Dashboard</h2>
                    <p className="exec-summary__subtitle">{projectNames || "Global Portfolio"}</p>
                </div>
                <button className="exec-summary__risk-btn">Simulate Risk Event</button>
            </div>

            {/* Summary Cards */}
            <div className="exec-summary__cards">
                {summaryCards.map((card, i) => (
                    <div className="exec-summary__card" key={i}>
                        <span className="exec-summary__card-label">{card.label}</span>
                        <span className="exec-summary__card-value">{card.value}{card.suffix}</span>
                    </div>
                ))}
            </div>

            {/* Data Tables */}
            {tableConfig.map((t, idx) => (
                <div className="exec-summary__section" key={idx}>
                    <div className="exec-summary__section-header">
                        <h3 className="exec-summary__section-title">{t.title}</h3>
                        <button
                            className="exec-summary__add-btn"
                            onClick={() => navigate(t.addPath)}
                        >
                            <BsPlus size={16} />
                            {t.addLabel}
                        </button>
                    </div>
                    <div className="exec-summary__table-wrap">
                        <Table
                            bordered
                            size="small"
                            rowKey={(record: any) => record?.id ?? `${idx}-${Math.random()}`}
                            scroll={{ x: "max-content" }}
                            loading={t.loading}
                            columns={t.cols}
                            dataSource={t.data}
                            pagination={{ pageSize: 5, size: "small" }}
                            locale={{ emptyText: <span className="text-muted">No data available</span> }}
                        />
                    </div>
                </div>
            ))}

            {/* Data Dictionary */}
            <div className="exec-summary__dict">
                <div className="exec-summary__dict-header">
                    <h3 className="exec-summary__dict-title">System: Data Dictionary</h3>
                </div>
                <ul className="exec-summary__dict-list">
                    <li><b>Vintage:</b> The year carbon was sequestered</li>
                    <li><b>Buffer %:</b> Risk reserve credits held by registry</li>
                    <li><b>Baseline:</b> Initial carbon state before project start</li>
                    <li><b>Family Office:</b> Internal management of wealth/allocation</li>
                </ul>
                <button className="exec-summary__dict-close">Close</button>
            </div>
        </div>
    );
};

export default ExecutiveSummary;
